import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Cloud, FolderOpen, Loader2 } from 'lucide-react';
import type { IngestSourceSummary } from '@/hooks/useIngestedDocuments';
import { statusLabel, statusTone } from '@/lib/driveIngestLogic';
import { providerLabel } from '@/lib/ingestedDocsLogic';
import { useLocalization } from '@/hooks/useLocalization';

interface Props {
  sources: IngestSourceSummary[];
  loading: boolean;
  error: string | null;
  tenantSlug: string | null;
  showManageLink?: boolean;
}

const IngestSourcesStrip = ({ sources, loading, error, tenantSlug, showManageLink = true }: Props) => {
  const { tx, t } = useLocalization();
  const integrationsPath = tenantSlug ? `/${tenantSlug}/integrations` : '/integrations';

  return (
    <section aria-label={t('Data sources')} className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold">{t('Data sources')}</h3>
        {showManageLink && <Link to={integrationsPath} className="text-xs text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded">
          
          {t('Manage on Integrations')}
        </Link>}
      </div>

      {loading && sources.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />  {t('Loading data sources…')}
        </div>
      ) : error ? (
        <p className="text-sm text-muted-foreground" role="status">{error}</p>
      ) : sources.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg px-3 py-4">
          <p>{t('No data sources connected.')}</p>
          <p className="text-xs mt-1">
            
            {t('An admin can connect Google Drive or Microsoft 365 folders on the')}{' '}
            <Link to={integrationsPath} className="text-primary hover:underline">{t('Integrations page')}</Link>.
          </p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {sources.map((s) => {
            const reauth = s.last_status === 'needs_reauth';
            const label = statusLabel(s.last_status);
            const lastSynced = s.last_synced_at ? new Date(s.last_synced_at).toLocaleString() : 'not synced yet';
            return (
              <li key={s.id} className="border border-border rounded-lg p-3 space-y-1.5 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <Cloud className="w-4 h-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      {tx(providerLabel(s.provider))}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0" title={s.display_path}>
                      <FolderOpen className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                      <span className="truncate">{s.display_path}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                    {!s.enabled && <Badge variant="outline" className="text-[10px]">{t('Paused')}</Badge>}
                    {label && <Badge variant={statusTone(s.last_status)} className="text-[10px]">{tx(label)}</Badge>}
                  </div>
                </div>
                {reauth && (
                  <p className="text-xs text-destructive">
                    
                    {t('Access expired. An admin needs to reconnect this source on the Integrations page.')}
                  </p>
                )}
                {s.last_status === 'failed' && s.last_error && (
                  <p className="text-xs text-destructive break-words">{s.last_error}</p>
                )}
                {!s.integration_active && !reauth && (
                  <p className="text-xs text-muted-foreground">{t('The provider connection is inactive.')}</p>
                )}
                <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{s.files_total}  {t('files')}</span>
                  <span>{s.files_analyzed}  {t('analyzed')}</span>
                  <span>{s.chunks_total}  {t('searchable chunks')}</span>
                  {s.files_failed > 0 && <span className="text-destructive">{s.files_failed}  {t('failed')}</span>}
                  {s.files_removed > 0 && <span>{s.files_removed}  {t('removed')}</span>}
                  <span>{t('Last synced:')} {lastSynced}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default IngestSourcesStrip;
